import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { notificationApi, notificationKeys } from "@/features/notifications/api";

/**
 * How often the bell asks whether anything is new.
 *
 * A notification is not a message somebody is waiting on an answer to: it
 * says a thing has happened, and the thing is still there a minute later.
 * So this asks once a minute, and not at all while the tab is in the
 * background.
 */
const POLL_MS = 60_000;

/** How many are unread, for the number on the bell. */
export function useUnreadNotifications() {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: notificationApi.unread,
    refetchInterval: POLL_MS,
  });
}

/**
 * The most recent notifications, newest first.
 *
 * Read only while the list is on screen. Opening it always asks afresh, so
 * what is shown is never the list as it stood the last time it was open.
 */
export function useNotifications(open: boolean, limit = 20) {
  return useQuery({
    queryKey: notificationKeys.list(limit),
    queryFn: () => notificationApi.list(limit),
    enabled: open,
    staleTime: 0,
    refetchInterval: open ? POLL_MS : false,
  });
}

/** Reading changes both the list and the count, so both are asked for again. */
function useNotificationMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkNotificationRead() {
  return useNotificationMutation((id: string) => notificationApi.markRead(id));
}

export function useMarkAllNotificationsRead() {
  return useNotificationMutation(() => notificationApi.markAllRead());
}
